import { ISbCache, ISbStories } from 'storyblok-js-client';

import { Metadata } from 'next/types';

import { Robots } from 'next/dist/lib/metadata/types/metadata-types';

import type {
  IconNamesUnion,
  LinkStoryblok,
  ProjectSlug,
  TailwindBackground,
} from '@monoimin/sb-types';

export enum LOCALES {
  RU = 'ru',
  EN = 'en',
  DE = 'de',
}

export enum DATASOURCE_TYPES {
  IMIN_BLOG = 'imin-blog',
  IMIN_INSIDER = 'imin-insider',
  IMIN_VIDEO = 'imin-video',
  GOLDEN_VISA = 'golden-visa',
}

export enum DATASOURCE_CATEGORY_PREFIXES {
  IMIN_BLOG = '[IMIN-BLOG]',
  IMIN_INSIDER = '[IMIN-INSIDER]',
  IMIN_VIDEO = '[IMIN-VIDEO]',
  GOLDEN_VISA = '[GOLDEN-VISA]',
}

export const DATASOURCE_CATEGORY_PREFIXES_BY_TYPE = {
  [DATASOURCE_TYPES.IMIN_BLOG]: DATASOURCE_CATEGORY_PREFIXES.IMIN_BLOG,
  [DATASOURCE_TYPES.IMIN_INSIDER]: DATASOURCE_CATEGORY_PREFIXES.IMIN_INSIDER,
  [DATASOURCE_TYPES.IMIN_VIDEO]: DATASOURCE_CATEGORY_PREFIXES.IMIN_VIDEO,
  [DATASOURCE_TYPES.GOLDEN_VISA]: DATASOURCE_CATEGORY_PREFIXES.GOLDEN_VISA,
} as const;

export enum CBI_GUIDE_LINK_ROLES {
  PARENT = 'parent',
  CHILD = 'child',
}

export enum STORY_VERSIONS {
  DRAFT = 'draft',
  PUBLISHED = 'published',
}

export enum STORYBLOK_BASE_API_URLS {
  V1 = 'https://api.storyblok.com/v1/cdn/',
  V2 = 'https://api.storyblok.com/v2/cdn/',
}

type IconMapping = {
  [key in NonNullable<LinkStoryblok['icon']>]: IconNamesUnion;
};

export const NEW_ICON_LINK_ICONS: IconMapping = {
  facebookLogo: 'facebook',
  telegramLogo: 'telegram',
  instagramLogo: 'instagram',
  linkedInLogo: 'linkedIn',
  twitterLogo: 'X (twitter)',
  youtubeLogo: 'youtube',
  whatsappLogo: 'whatsapp',
  phone: 'phone',
  arrowRight: 'arrowRight',
  plane: 'plane',
  investment: 'investment',
  marriage: 'marriage',
  warning: 'warning',
} as const;

export const MAP_STORYBLOK_BACKGROUNDS_TO_TAILWIND: TailwindBackground = {
  'bg-grey': 'bg-grey',
  'bg-blueGrey': 'bg-blueGrey',
};

export const ARTICLE_FOLDERS = ['blog', 'cases', 'insider', 'news'] as const;

// это источник всего что связана со слагами минисайтов, стипами с массивами, сперва здесь делаем изменения если нужно добавить либо убрать слаг, потом в других местах чтобысоответствовали этому списку слагов
export const minisiteOriginBySlug = {
  'imin-malta': 'https://imin.blog',
  'imin-cyprus': 'https://imin-cyprus.com',
  iminproperty: 'https://iminproperty.vercel.app',
  property: 'https://property.passportivity.com',
  'imin-portugal': 'https://imin-portugal.vercel.app',
  'imin-caribbean': 'https://imin-caribbean.vercel.app',
  'imigrant-hungary': 'https://imigrant-hungary.vercel.app',
  'immigrant-austria': 'https://immigrant-austria.vercel.app',
};

export const MINISITE_SLUGS: MiniSlug[] = Object.keys(
  minisiteOriginBySlug
) as MiniSlug[];

export const minisiteSlugByOrigin = Object.fromEntries(
  Object.entries(minisiteOriginBySlug).map(([key, value]) => [value, key])
);
export const PROPERTY_PLACEHOLDER_IMAGE = {
  src: '/images/real-estate/real-estate-placeholder.png',
  transparentSrc: '/images/real-estate/real-estate-transparent-placeholder.png',
  alt: 'Real Estate placeholder',
};

export const ENABLED_ROBOTS = {
  index: true,
  follow: true,
};
export const ENABLED_ARTICLE_ROBOTS: Robots = {
  index: true,
  follow: true,
  'max-video-preview': 'none',
  'max-image-preview': 'large', // Your specific directive here
  'max-snippet': -1,
};
export const ENABLED_ROBOTS_WITH_VERIFICATION = {
  index: true,
  follow: true,
};
export const ENABLED_ROBOTS_FOR_ARCHIVED_PAGES = {
  index: false,
  follow: true,
};

export const DISABLED_ROBOTS = {
  index: false,
  follow: false,
};

export const PROJECT_SLUGS = {
  MONOIMIN: 'monoimin',
  PASSPORTIVITY: 'passportivity',
  PASSPORT_RATING: 'passport-rating',
  GOLDEN_VISA: 'golden-visa',
  MINISITES: 'minisites',
} as const;

export const SB_FOLDER_BY_PROJECT_SLUG: Record<ProjectSlug, string> = {
  monoimin: 'immigrantinvest',
  minisites: 'imin-minisites',
  'golden-visa': 'goldenvisa-io',
  'passport-rating': 'passport-rating',
  passportivity: 'passportivity-com',
};

export const N_DASH = String.fromCharCode(8212);

export const EN_ALPHABET_AND_NUMBERS = 'abcdefghijklmnopqrstuvwxyz0123456789';

export const CHANGED_CATEGORIES_SLUGS = {
  'imin-blog': {
    'real-estate-investment': 'real-estate',
    'real-estate-investment-ru': 'real-estate',
    'how-they': 'living-abroad',
  },
};

export const ARTICLE_SLUGS_BY_TYPE = {
  case: 'cases',
  article: 'blog',
  insider: 'insider',
} as const;

export const ARTICLE_MAIN_PAGE_TITLE_DICT_KEYS = {
  case: 'allCases',
  article: 'blog',
  insider: 'newsAndAnalytics',
} as const;

export const EXPERTS_MAIN_EXCLUDING_FIELDS = [
  'body',
  'seo',
  'lead',
  'investmentProgram',
  'programPage',
  'author',
  'cardQuote',
  'title',
  'oldSlug',
  'readMore',
  'blogLevel',
  'readMoreTitle',
  'statusService',
  'authorDescription',
].join(',');

export const INVESTMENT_PROGRAM_COMPARISON_FIELDS = [
  'price',
  'factsheet',
  'status',
  'residencyAfterStatus',
  'submitBiometrics',
  'visafreeCountries',
  'visafreeSchengen',
  'visafreeUSA',
  'visafreeUK',
  'visafreeAustralia',
  'familyParticipation',
  'addFamilyMembers',
  'euRightsLivingStudyWork',
  'heritability',
  'taxResidencyChange',
  'dualCitizenshipAllowed',
  'investmentOptions',
  'totalExpensesApplicant',
  'totalExpensesFamily',
  'investmentHoldingPeriod',
  'investmentReturnPeriod',
  'sponsorship',
  'documentType',
  'validity',
  'receivingMaintainingConditions',
].join(',');

export const INSIDER_MAIN_EXCLUDING_FIELDS = [
  'seo',
  'investmentProgram',
  'programPage',
  'author',
  'cardQuote',
  'title',
  'oldSlug',
  'readMore',
  'blogLevel',
  'readMoreTitle',
  'statusService',
  'authorDescription',
].join(',');

/**
 * Используется для обработки не успешного ответа метода getStories.
 */
export const GET_STORIES_EMPTY_RESPONSE: ISbStories = {
  data: { stories: [], rels: [], links: [], cv: 0 },
  total: 0,
  perPage: 0,
  headers: undefined,
};

/**
 * Используется для обработки не успешного ответа метода getStory.
 */
export const GET_STORY_EMPTY_RESPONSE = {
  data: { story: null, rels: [], links: [], cv: 0 },
};

export const STORYBLOK_TOKENS = {
  server: {
    preview: process.env['PREVIEW_SECRET_TOKEN'],
    api: process.env['STORYBLOK_API_TOKEN'],
    oauth: process.env['STORYBLOK_OUATH_TOKEN'],
  },
  client: {
    preview: process.env['NEXT_PUBLIC_PREVIEW_SECRET_TOKEN'],
    api: process.env['NEXT_PUBLIC_STORYBLOK_API_TOKEN'],
  },
};

export const STORYBLOK_CACHE_PARAMS: ISbCache = {
  type: 'memory',
  clear: 'auto',
};

export const STORYBLOK_PREVIEW_CACHE_PARAMS: ISbCache = {
  type: 'none',
  clear: 'auto',
};

export const STORYBLOK_SPACE_ID = 176292;

export const DEV_HOST = 'http://0.0.0.0:1337';

export * from './regex';
export * from './environments';
export * from './sitemap';
export * from './revalidation';
export * from './intl';
export * from './form';

export const DEFAULT_IMIN_METADATA: Metadata = {
  openGraph: {
    siteName: 'Immigrant Invest',
    type: 'article',
  },
  twitter: {
    card: 'summary_large_image',
    creator: '@Immigrantinvest',
    site: '@Immigrantinvest',
  },
};

export const E2E_TAGS = {
  SMOKE: '@smoke',
  DEEP: '@deep',
  SCHEDULE: '@schedule',
} as const;

export const STORAGE_KEYS = {
  SUBSCRIBE_POPUP: {
    CLOSED: 'SubscribeFormPopup_Closed',
    SENT: 'SubscribeFormPopup_Sent',
  },
  FACTSHEET_POPUP: {
    CLOSED: 'FactsheetPopup_Closed',
    SENT: 'FactsheetPopup_Sent',
  },
  DEVICE_INFO: 'deviceInfo',
} as const;

export const IMIN_SITEMAP_NAMES = [
  'blog',
  'careers',
  'cases',
  'cbi-guide',
  'events',
  'experts',
  'guides',
  'insider',
  'pages',
  'podcast',
  'price',
  'factsheet',
  'programs',
  'real-estate',
  'video',
  'workshops',
  'quiz',
  'media',
  'policy',
];
export const DISABLED_ROBOTS_RULES = {
  userAgent: '*',
  disallow: '/',
};

// Domains allowed to have robots.txt enabled for minisites
export const ALLOWED_MINISITE_DOMAINS = ['imin-cyprus.com', 'imin-malta.com'];

export const OPENGRAPH_IMAGE_SIZES = {
  big: {
    width: 1200,
    height: 630,
  },
  howTo: {
    width: 250,
    height: 320,
  },
};

export const countryCodes = [
  'AF',
  'AL',
  'DZ',
  'AD',
  'AO',
  'AG',
  'AR',
  'AM',
  'AU',
  'AT',
  'AZ',
  'MD',
  'BS',
  'BH',
  'BD',
  'BB',
  'BY',
  'MM',
  'BE',
  'BZ',
  'BJ',
  'BT',
  'BO',
  'BA',
  'BW',
  'BR',
  'BN',
  'BG',
  'BF',
  'BI',
  'KH',
  'CM',
  'CA',
  'CV',
  'CF',
  'TD',
  'CL',
  'CN',
  'CO',
  'KM',
  'CG',
  'CD',
  'CR',
  'CI',
  'HR',
  'CU',
  'CY',
  'CZ',
  'DK',
  'RS',
  'DJ',
  'SN',
  'DM',
  'DO',
  'EC',
  'SI',
  'EG',
  'SV',
  'GQ',
  'ER',
  'EE',
  'SZ',
  'LK',
  'ET',
  'FJ',
  'FI',
  'FR',
  'GA',
  'GM',
  'GE',
  'DE',
  'GH',
  'GR',
  'GD',
  'GT',
  'GN',
  'GW',
  'GY',
  'HT',
  'HN',
  'HU',
  'IS',
  'IN',
  'ID',
  'IR',
  'IQ',
  'IE',
  'IL',
  'IT',
  'JM',
  'JP',
  'JO',
  'ZW',
  'KZ',
  'KE',
  'KI',
  'KW',
  'KG',
  'LA',
  'LV',
  'LB',
  'LS',
  'LR',
  'LY',
  'LI',
  'LT',
  'LU',
  'MG',
  'MW',
  'MY',
  'MV',
  'ML',
  'MT',
  'MH',
  'MR',
  'MU',
  'MX',
  'FM',
  'MC',
  'MN',
  'ME',
  'MA',
  'MZ',
  'NA',
  'NR',
  'NP',
  'NL',
  'NZ',
  'NI',
  'NE',
  'NG',
  'KP',
  'MK',
  'NO',
  'OM',
  'PK',
  'PW',
  'PA',
  'PG',
  'PY',
  'PE',
  'PH',
  'PL',
  'PT',
  'QA',
  'RO',
  'RU',
  'RW',
  'KN',
  'LC',
  'WS',
  'SM',
  'ST',
  'SA',
  'SC',
  'SL',
  'SG',
  'SK',
  'SB',
  'SO',
  'ZA',
  'KR',
  'SS',
  'ES',
  'VC',
  'SD',
  'SR',
  'SE',
  'CH',
  'SY',
  'TJ',
  'TZ',
  'TH',
  'TL',
  'TG',
  'TO',
  'TT',
  'TN',
  'TR',
  'TM',
  'TV',
  'UG',
  'UA',
  'AE',
  'GB',
  'US',
  'UY',
  'UZ',
  'VU',
  'VE',
  'VN',
  'YE',
  'ZM',
];
